import { useLocalize } from "@faro-lotv/foreign-observers";
import { DateTimeUtils } from "@stellar/utils";

/**
 * Formats a date following the locale-specific CLDR medium date format.
 * Omits the year when the date is in the current year and the shouldShowYear flag is false.
 */
export type FormatDate = (date: DateTimeUtils.DateTimeInput) => string;

export interface FormattedDateWithTooltip {
  /** The display string: relative (e.g. "2 days ago") for recent dates, absolute for older */
  displayDate: string;

  /** The absolute date string, only provided when the display is relative */
  tooltipDate: string | undefined;
}


interface UseDateTime {
  /**
   * Formats a date following the locale-specific CLDR medium date format.
   * Omits the year when the date is in the current year and the shouldShowYear flag is false.
   */
  formatDate: FormatDate;

  /**
   * Formats a date with relative display for dates within the last 7 days,
   * falling back to absolute for older dates. Returns both the display string
   * and the full absolute date for tooltip use.
   */
  formatDateWithTooltip: (date: DateTimeUtils.DateTimeInput ) => FormattedDateWithTooltip;
}

/**
 * Hook to format and translate dates
 *
 * Uses locale-specific absolute dates per CLDR medium date format,
 * For dates within the last 7 days, provides relative formatting
 * with an absolute-date tooltip for hover display.
 *
 * @returns Formatting functions bound to the current user language
 */
export function useDateTime(): UseDateTime {
  const { currentLanguage } = useLocalize();
  const languageCode = currentLanguage.code;

  return {
    formatDate: (date) =>
      DateTimeUtils.formatAbsoluteDate({ date, languageCode }),

    formatDateWithTooltip: (date) => {
      const { formattedDate, absoluteDate } = DateTimeUtils.formatDateDisplay({ date, languageCode });

      return {
        displayDate: formattedDate,
        tooltipDate: absoluteDate,
      };
    },
  };
}
